// «Elige el teléfono»: una tarjeta por modelo de teléfono (capacidad y color dentro); los kits van en
// «Arma la venta». Tocar muestra el detalle; «Continuar» aparta el SKU con sus promos y avanza.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, PackageX, SearchX } from 'lucide-react';
import { Button, ConfirmDialog } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import {
    useCatalog,
    useRenewLineReservation,
    useScoreConsultSilent,
    useSelectProduct,
    useWizardSession,
} from '../api/useWizard';
import { accountAlreadyOpen } from '../accountWait';
import { anchorLineOf } from '../buildSale';
import {
    cardCreditBubbles,
    cardCreditOf,
    sureKeepFinances,
    creditTermsFor,
    downPaymentNote,
    type LevelCreditTerms,
} from '../cardBenefits';
import {
    FILTROS_VACIOS as EMPTY_FILTERS,
    aplicarFiltros as applyFilters,
    financiersOf,
    marcasDe as brandsOf,
    terminosDe as termsOf,
    type CatalogFilterState,
} from '../catalogFilters';
import {
    creditPromotionRows,
    creditPromotionsForItem,
    type CreditPromotionRow,
} from '../creditPromotions';
import { IntentKeys } from '../intentKeys';
import { isTransientRequestError } from '../deviceEnrollment';
import { needsCatalogReload } from '../lineErrors';
import { forgetLinkedSku, peekLinkedSku } from '../linkedSku';
import { previousStepSlug, stepAfterCheckout } from '../steps';
import {
    catalogForSession,
    isSureKeepAvailable,
    isSureKeepConfirmed,
} from '../sureKeepAvailability';
import { isReservationLiveNow } from '../reservationClock';
import { phoneLineOf, phoneOffersOf } from '../productSuggestions';
import {
    LINE_CHANGE_ACTION_LABEL,
    RENEW_UNAVAILABLE_MESSAGE,
    lineChangeAction,
    renewFallbackOf,
    reservationErrorMessage,
    swapErrorMessage,
    type LineChangeAction,
} from '../reservationErrors';
import { CatalogToolbar } from '../components/CatalogToolbar';
import { ProductCard, ProductCardSkeleton } from '../components/ProductCard';
import { ModelCard } from '../components/ModelCard';
import {
    catalogEntries,
    type CatalogEntry,
    type ModelGroup,
} from '@/domains/sales/portal/catalog/lib/modelGroups';
import { ProductDetailCard, type AccessoryPromotionNote } from '../components/ProductDetailCard';
import type { CardPromo } from '../components/CardPricing';
import { HowItWorksButton } from '../components/HowItWorksModal';
import { StorePromosBanner } from '../components/StorePromosBanner';
import { chosenVariantOf, useVariantMemory } from '../variantMemory';
import { KitContentsModal } from '../components/KitContentsModal';
import { kitComponentCount } from '../kitContents';
import { customerFirstName } from '../referrals';
import { SaveAndExitButton, WizardBackLink } from '../components/WizardFootNav';
import {
    saleQuoteStateOf,
    type BenefitBubble,
    type CardSaleBenefits,
    type PhonePricing,
    type RenderAccountBonuses,
    type SessionRefreshContext,
    type StorePromotionGroups,
} from '../promotionSlots';
import type { CatalogItem, PlanLevel, ReservedLine, WizardSession } from '../types';

const SKELETONS = 6;

/** Qué descartaría la cotización: un cambio de promos o de teléfono. */
type DiscardReason = 'promotions' | 'phone';
const DISCARD_COPY: Record<DiscardReason, string> = {
    promotions:
        'Cambiar las promos o el código descarta la cotización de crédito; tendrás que cotizar de nuevo.',
    phone: 'Cambiar el teléfono descarta la cotización de crédito; tendrás que cotizar de nuevo.',
};

/** Lugares para las piezas de Promociones (REQ-134); sin pieza no dejan hueco. */
export interface PhoneSelectSlots {
    /** Aviso «Llegó recomendado»: quién lo recomendó y lo que gana el cliente. */
    referredByNotice?: (customerName: string) => ReactNode;
    /** Burbujas de promos propias y precio con descuento de cada tarjeta. */
    saleBenefits?: (item: CatalogItem) => CardSaleBenefits;
    /** «Promo del equipo» en el detalle: marca las promos que se apartan al continuar. */
    promotionPicker?: (item: CatalogItem, ctx: PhoneDetailContext) => ReactNode;
    /** Promos del detalle en solo lectura, debajo del selector y sin repetir las suyas. */
    detailPromotions?: (
        item: CatalogItem,
        ctx: PhoneDetailContext,
        renderBonuses: RenderAccountBonuses,
    ) => ReactNode;
    /** Cajas del encabezado: promos de toda la tienda y las que solo bajan accesorios. */
    storePromotionGroups?: StorePromotionGroups;
    /** Lista corta de promos de cada tarjeta, sacada de las de la tienda por alcance del SKU. */
    cardPromotions?: (item: CatalogItem) => readonly CardPromo[];
    /** Promos sobre accesorios que el teléfono activa; se aplican en «Arma la venta». */
    accessoryPromotions?: (item: CatalogItem) => readonly AccessoryPromotionNote[];
    /** Precio del elegido con lo marcado; `heldLine` es su línea si ya está apartado. */
    pricing?: (item: CatalogItem, heldLine: ReservedLine | undefined) => PhonePricing;
    /** «¿Código?» en el pie del detalle. */
    discountCode?: (ctx: { sku: string; disabled: boolean }) => ReactNode;
    /** Con el teléfono ya apartado, aparta lo marcado y valida el código. */
    commitPromotions?: (ctx: PhoneCommitContext) => Promise<PhoneCommitOutcome>;
}

/** Lo que el detalle sabe del equipo elegido. */
export interface PhoneDetailContext extends SessionRefreshContext {
    /** Línea del equipo si ya está apartado en la venta. */
    heldLineId?: string;
    /** Kit apartado o venta cobrada: las promos se ven, no se cambian. */
    readOnly: boolean;
}

/** Lo que el paso sabe al continuar, con el teléfono ya apartado. */
export interface PhoneCommitContext {
    /** La venta como quedó después de apartar, renovar o avanzar. */
    session: WizardSession;
    anchorLineId: string;
    /** Se renovó, cambió o agregó el teléfono: lo marcado se vuelve a apartar aunque no cambie. */
    reapply: boolean;
    /** Kit apartado: sin selector, solo se reaplica lo que ya tenía. */
    readOnly: boolean;
    /** `true` si se puede escribir; con cotización viva antes pregunta si se descarta. */
    confirmQuoteDiscard: () => Promise<boolean>;
}

export interface PhoneCommitOutcome {
    /** El vendedor se queda en el paso (código rechazado, error o no quiso descartar). */
    stay: boolean;
}

/** El teléfono quedó apartado: la venta fresca y su línea. */
interface SecuredPhone {
    session: WizardSession;
    lineId: string | undefined;
    reapply: boolean;
}

/** La pieza de la sesión cuenta como disponible: es la que ya tiene apartada esta venta. */
function withHeldUnit(items: readonly CatalogItem[], heldSku: string | undefined): CatalogItem[] {
    return items.map((item) =>
        item.sku === heldSku && (item.stock ?? 1) <= 0 ? { ...item, stock: 1 } : item,
    );
}

/** El teléfono que trae un kit, si el kit trae uno solo y está en el catálogo. */
function kitPhoneSku(kit: CatalogItem, items: readonly CatalogItem[]): string | undefined {
    const phones = (kit.components ?? []).filter(
        (c) => items.find((i) => i.sku === c.sku)?.category === 'PHONE',
    );
    return phones.length === 1 ? phones[0]?.sku : undefined;
}

/** Atajo `/` para saltar al buscador, salvo que ya se esté escribiendo en un campo. */
function useSearchShortcut(searchRef: React.RefObject<HTMLInputElement | null>): void {
    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== '/' || e.ctrlKey || e.metaKey) return;
            const active = document.activeElement;
            if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
            e.preventDefault();
            searchRef.current?.focus();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, [searchRef]);
}

export function PhoneSelectStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    slots?: PhoneSelectSlots;
}) {
    const navigate = useNavigate();
    // `?sku=` de «Iniciar venta» en el catálogo del portal.
    const { sku: urlSku } = useSearch({ from: '/wizard' });
    // Sin `?sku=` en la URL, el que se ligó a la sesión al crearla; se consume una sola vez.
    const [storedSku] = useState(() => peekLinkedSku(sessionId));
    useEffect(() => {
        forgetLinkedSku(sessionId);
    }, [sessionId]);
    const linkedSku = urlSku ?? storedSku;
    const {
        data: session,
        refresh: refreshSession,
        refetch: refetchSession,
    } = useWizardSession(sessionId);
    const { data: rawCatalog, isLoading, isFetching, isError, refetch } = useCatalog(sessionId);
    const sureKeep = isSureKeepConfirmed(session);
    const catalog = useMemo(() => catalogForSession(rawCatalog, sureKeep), [rawCatalog, sureKeep]);
    // Las promos se muestran desde antes del score; solo un cliente que no califica las pierde.
    const notEligible = !isSureKeepAvailable(session);
    const promoCatalog = useMemo(
        () => catalogForSession(rawCatalog, !notEligible),
        [rawCatalog, notEligible],
    );
    const levelPending = !notEligible && !sureKeep;
    const selectProduct = useSelectProduct(sessionId);
    const renewReservation = useRenewLineReservation(sessionId);
    // Cubre toda la renovación, incluida la relectura y el apartado de respaldo.
    const renewingRef = useRef(false);
    const [renewing, setRenewing] = useState(false);
    const scoreConsultSilent = useScoreConsultSilent(sessionId);
    // Un solo apartado en vuelo: el doble clic en «Continuar» no aparta dos unidades.
    const reservingRef = useRef(false);
    const [intentKeys] = useState(() => new IntentKeys());

    // Score adelantado y silencioso: marca el catálogo con el nivel antes de la forma de pago.
    const scoreTriggeredRef = useRef(false);
    useEffect(() => {
        if (scoreTriggeredRef.current) return;
        if (!session || !accountAlreadyOpen(session.accountStatus) || session.loan?.level) return;
        scoreTriggeredRef.current = true;
        scoreConsultSilent.mutate(undefined, { onError: () => undefined });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- dispara una sola vez por montaje/condición
    }, [session]);

    const [filters, setFilters] = useState<CatalogFilterState>(EMPTY_FILTERS);
    const searchRef = useRef<HTMLInputElement>(null);
    useSearchShortcut(searchRef);
    // `undefined` = el vendedor no ha tocado nada y manda el deep-link o el teléfono de la venta.
    const [touchedSku, setTouchedSku] = useState<string | undefined>(undefined);
    const [kitContentsSku, setKitContentsSku] = useState<string | undefined>(undefined);
    // Teléfono que reemplazaría al kit apartado, a la espera de que el vendedor lo confirme.
    const [kitReplaceFor, setKitReplaceFor] = useState<CatalogItem | undefined>(undefined);
    // Promos de un cambio con cotización viva: resuelve si el vendedor acepta descartarla.
    const [discardPrompt, setDiscardPrompt] = useState<
        { resolve: (ok: boolean) => void; reason: DiscardReason } | undefined
    >();
    const [committing, setCommitting] = useState(false);

    // NOT_ELIGIBLE se trata como «nivel resuelto, sin nivel» para las promociones.
    const clientLevel: PlanLevel | null | undefined =
        session?.loan?.level === undefined
            ? undefined
            : session.loan.level === 'NOT_ELIGIBLE'
              ? null
              : session.loan.level;

    const sessionPhoneLine = phoneLineOf(session);
    const sessionPhoneSku = sessionPhoneLine?.sku;
    // Por SKU: releer la sesión no recrea el catálogo ni el borrador de promos.
    const heldSku = useMemo(() => phoneLineOf(session)?.sku, [session]);
    const items = useMemo(() => withHeldUnit(catalog?.items ?? [], heldSku), [catalog, heldSku]);
    // Teléfonos y kits: los kits solo se buscan (ancla de la venta o `?sku=`), no van en la cuadrícula.
    const offers = useMemo(() => phoneOffersOf(items, 'ALL'), [items]);
    // La cuadrícula lleva solo teléfonos, también las variantes agotadas (deshabilitadas en su tarjeta).
    const typeOffers = useMemo(
        () => phoneOffersOf(items, 'PHONE', { keepSoldOutPhones: true }),
        [items],
    );
    const visible = useMemo(() => applyFilters(typeOffers, filters), [typeOffers, filters]);
    const entries = useMemo(() => catalogEntries(visible), [visible]);
    const totalEntries = useMemo(() => catalogEntries(typeOffers).length, [typeOffers]);
    // Variante que el vendedor dejó elegida en cada modelo; se recuerda en la pestaña.
    const modelGroups = useMemo(
        () =>
            catalogEntries(typeOffers).flatMap((entry) =>
                entry.kind === 'model' ? [entry.group] : [],
            ),
        [typeOffers],
    );
    const { memory: variantMemory, remember: rememberVariant } = useVariantMemory(
        sessionId,
        modelGroups,
    );
    // Las opciones del filtro salen de lo que muestra la cuadrícula, agotados incluidos.
    const brands = useMemo(() => brandsOf(typeOffers), [typeOffers]);
    const financiers = useMemo(() => financiersOf(typeOffers), [typeOffers]);
    const terms = useMemo(() => termsOf(filters.q), [filters.q]);
    const promoItems = useMemo(
        () => new Map((promoCatalog?.items ?? []).map((item) => [item.sku, item])),
        [promoCatalog],
    );
    /** El equipo como lo ven las promos: con SureKeep aunque el nivel no esté resuelto. */
    const promoItem = (item: CatalogItem): CatalogItem => promoItems.get(item.sku) ?? item;
    const promoLevel = sureKeep ? clientLevel : undefined;
    const scopeWide = useMemo(() => rawCatalog?.scopeWidePromotions ?? [], [rawCatalog]);
    const creditRows = useMemo(
        () => creditPromotionRows(scopeWide, offers, promoLevel),
        [scopeWide, offers, promoLevel],
    );
    const storeCreditLines = notEligible
        ? []
        : creditRows
              .filter((row) => row.reach.kind === 'store')
              .map((row) => ({
                  key: row.promotionId,
                  name: row.name,
                  customerText: row.description,
              }));
    const creditRowsFor = (item: CatalogItem): CreditPromotionRow[] =>
        sureKeepFinances(promoItem(item)) ? creditPromotionsForItem(creditRows, item) : [];
    const creditTerms: LevelCreditTerms = {
        ...(catalog?.minDownPaymentPct !== undefined && { minPct: catalog.minDownPaymentPct }),
        ...(catalog?.creditCapCents !== undefined && { capCents: catalog.creditCapCents }),
    };

    // Un ?sku= de accesorio o de kit sin teléfono no es un equipo de esta pantalla: se ignora.
    const offered = (sku: string | undefined): string | undefined =>
        sku !== undefined && offers.some((i) => i.sku === sku) ? sku : undefined;
    // Un ?sku= de kit entra con el teléfono que trae el kit.
    const linkedPhone = (sku: string | undefined): string | undefined => {
        const item = offers.find((i) => i.sku === sku);
        return item?.category === 'BUNDLE' ? offered(kitPhoneSku(item, items)) : offered(sku);
    };
    const linkedPhoneSku = linkedPhone(linkedSku);
    const pickedSku = touchedSku ?? linkedPhoneSku ?? offered(sessionPhoneSku);
    const picked = offers.find((i) => i.sku === pickedSku) ?? null;
    // La línea de la sesión y el `?sku=` ganan hasta que el vendedor toca algo.
    const variantPriority =
        touchedSku === undefined
            ? { sessionSku: sessionPhoneSku ?? null, urlSku: linkedPhoneSku ?? null }
            : {};
    /** La variante de la tarjeta; `null` si nadie la ha elegido y la tarjeta dice «Desde». */
    const variantOf = (group: ModelGroup): CatalogItem | null =>
        chosenVariantOf(group, variantMemory, variantPriority);
    const changeVariant = (group: ModelGroup, item: CatalogItem): void => {
        rememberVariant(group.key, item.sku);
        setTouchedSku(item.sku);
    };
    const pickedHeldLine =
        picked !== null && picked.sku === sessionPhoneSku ? sessionPhoneLine : undefined;
    const anchorKit =
        sessionPhoneLine !== undefined && sessionPhoneLine.components.length > 0
            ? sessionPhoneLine
            : undefined;
    const charged = session !== undefined && saleQuoteStateOf(session) === 'charged';
    const pricing: PhonePricing | null =
        picked === null
            ? null
            : (slots.pricing?.(promoItem(picked), pickedHeldLine) ?? {
                  baseCents: picked.cashPriceCents,
                  rows: [],
                  totalCents: picked.cashPriceCents,
                  estimated: pickedHeldLine === undefined,
              });
    const pickedPriceCents = pricing?.totalCents ?? 0;
    const pricingSku = picked?.sku ?? '';
    // Sin las partes solo SureKeep: lo que paga de contado.
    const sureKeepOnlyCents =
        pricing?.rows.filter((r) => r.sureKeepOnly).reduce((sum, r) => sum + (r.cents ?? 0), 0) ??
        0;
    const pickedCredit = picked ? cardCreditOf(picked, pickedPriceCents, creditTerms) : null;
    const pickedDownPayment =
        pickedCredit?.kind === 'surekeep' && pickedCredit.downPayment
            ? {
                  cents: pickedCredit.downPayment.cents,
                  note: downPaymentNote(
                      pickedCredit.downPayment,
                      pickedPriceCents,
                      clientLevel,
                      picked ? creditTermsFor(picked, creditTerms) : creditTerms,
                  ),
              }
            : null;
    const busy = selectProduct.isPending || renewReservation.isPending || renewing || committing;
    const kitInModal = offers.find((i) => i.sku === kitContentsSku) ?? null;
    const isPickedKit = picked?.category === 'BUNDLE';
    const pickedReservation: 'held' | 'expired' | 'none' =
        pickedHeldLine === undefined
            ? 'none'
            : isReservationLiveNow(pickedHeldLine.expiresAt)
              ? 'held'
              : 'expired';
    const detailContext: PhoneDetailContext = {
        refreshSession: async () => {
            await refreshSession();
        },
        ...(pickedHeldLine && { heldLineId: pickedHeldLine.lineId }),
        readOnly: isPickedKit || charged,
    };

    const goToBuildSale = (): void => {
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'build-sale' },
        });
    };

    /** Avisa el error de apartar y relee la venta; con `replaces` el equipo anterior se queda. */
    const reportLineError = (error: unknown, item: CatalogItem, replaces?: string): void => {
        const message =
            replaces !== undefined ? swapErrorMessage(error) : reservationErrorMessage(error);
        const action = lineChangeAction(error);
        if (action === undefined) toast.error(message);
        else toast.error(message, { action: toastAction(action, item) });
        void refreshSession();
        if (needsCatalogReload(error)) void refetch();
    };

    /** Aparta el equipo; con `replaces` cambia esa línea en la misma llamada (REQ-84). */
    const reserve = async (
        item: CatalogItem,
        replaces?: string,
    ): Promise<SecuredPhone | undefined> => {
        if (reservingRef.current) return undefined;
        reservingRef.current = true;
        // El reintento manual del mismo equipo reusa la llave: el silo no aparta dos veces.
        const intent = `${item.sku}|${replaces ?? ''}`;
        try {
            const fresh = await selectProduct.mutateAsync({
                sku: item.sku,
                qty: 1,
                ...(replaces !== undefined ? { replacesLineId: replaces } : {}),
                idempotencyKey: intentKeys.keyFor(intent),
            });
            intentKeys.settle(intent);
            const line =
                (fresh.reservedLines ?? []).find(
                    (l) => l.sku === item.sku && l.status !== 'RELEASING' && l.role !== 'GIFT',
                ) ?? anchorLineOf(fresh);
            return { session: fresh, lineId: line?.lineId, reapply: true };
        } catch (error) {
            // Solo un fallo pasajero reusa la llave; un rechazo del silo deja intentar de nuevo.
            if (!isTransientRequestError(error)) intentKeys.settle(intent);
            reportLineError(error, item, replaces);
            return undefined;
        } finally {
            reservingRef.current = false;
        }
    };

    /** Mismo teléfono con el apartado vencido: lo renueva sin pedir otra unidad. */
    const renew = async (item: CatalogItem, lineId: string): Promise<SecuredPhone | undefined> => {
        if (renewingRef.current) return undefined;
        renewingRef.current = true;
        setRenewing(true);
        try {
            const fresh = await renewReservation.mutateAsync(lineId);
            return { session: fresh, lineId, reapply: true };
        } catch (error) {
            const fallback = renewFallbackOf(error);
            if (fallback === 'replace') return await reserve(item, lineId);
            if (fallback === 'add') {
                await refreshSession();
                return await reserve(item);
            }
            if (fallback === 'unavailable') toast.error(RENEW_UNAVAILABLE_MESSAGE);
            else reportLineError(error, item);
            return undefined;
        } finally {
            renewingRef.current = false;
            setRenewing(false);
        }
    };

    /** Con cotización viva pregunta antes de cambiar promos o teléfono; cobrada no cambia promos. */
    const confirmQuoteDiscard = (reason: DiscardReason = 'promotions'): Promise<boolean> => {
        const state = session ? saleQuoteStateOf(session) : 'open';
        // El teléfono sí se cambia con folio: el silo borra el folio y rearma el cobro.
        if (state === 'open' || (state === 'charged' && reason === 'phone')) {
            return Promise.resolve(true);
        }
        if (state === 'charged') return Promise.resolve(false);
        return new Promise((resolve) => {
            setDiscardPrompt({ resolve, reason });
        });
    };
    const isQuoted = (): boolean => session !== undefined && saleQuoteStateOf(session) === 'quoted';
    const answerDiscard = (ok: boolean): void => {
        discardPrompt?.resolve(ok);
        setDiscardPrompt(undefined);
    };

    /** Ya apartado: aparta las promos marcadas y avanza; `discardConfirmed` si ya se preguntó. */
    const finish = async (
        { session: fresh, lineId, reapply }: SecuredPhone,
        discardConfirmed = false,
    ): Promise<void> => {
        if (lineId !== undefined && slots.commitPromotions) {
            setCommitting(true);
            try {
                const { stay } = await slots.commitPromotions({
                    session: fresh,
                    anchorLineId: lineId,
                    reapply,
                    readOnly: (fresh.reservedLines ?? []).some(
                        (l) => l.lineId === lineId && l.components.length > 0,
                    ),
                    confirmQuoteDiscard: discardConfirmed
                        ? () => Promise.resolve(true)
                        : () => confirmQuoteDiscard(),
                });
                if (stay) return;
            } finally {
                setCommitting(false);
            }
        }
        goToBuildSale();
    };

    /** «Continuar»: el mismo teléfono vigente avanza, vencido se renueva, otro se aparta o cambia. */
    const choose = async (
        item: CatalogItem,
        replacesLineId = session ? anchorLineOf(session)?.lineId : undefined,
        kitSwapConfirmed = false,
    ): Promise<void> => {
        if (anchorKit && item.sku !== anchorKit.sku && !kitSwapConfirmed) {
            setKitReplaceFor(item);
            return;
        }
        let secured: SecuredPhone | undefined;
        let discardConfirmed = false;
        if (item.sku === sessionPhoneSku && sessionPhoneLine !== undefined && session) {
            secured = isReservationLiveNow(sessionPhoneLine.expiresAt)
                ? { session, lineId: sessionPhoneLine.lineId, reapply: false }
                : await renew(item, sessionPhoneLine.lineId);
        } else {
            // Cambiar el teléfono descarta la cotización: se pregunta antes de tocar el apartado.
            if (!(await confirmQuoteDiscard('phone'))) return;
            discardConfirmed = isQuoted();
            secured = await reserve(item, replacesLineId);
        }
        if (secured) await finish(secured, discardConfirmed);
    };

    // «Cambiar» lee la venta fresca: el teléfono que ya estaba lo puso otra pantalla.
    const swapInto = async (item: CatalogItem): Promise<void> => {
        const { data: fresh } = await refetchSession();
        const anchor = fresh ? anchorLineOf(fresh) : undefined;
        if (fresh === undefined || anchor === undefined) return;
        const swapping = anchor.sku !== item.sku;
        if (swapping && !(await confirmQuoteDiscard('phone'))) return;
        const secured = swapping
            ? await reserve(item, anchor.lineId)
            : isReservationLiveNow(anchor.expiresAt)
              ? { session: fresh, lineId: anchor.lineId, reapply: false }
              : await renew(item, anchor.lineId);
        if (secured) await finish(secured, swapping && isQuoted());
    };

    const toastAction = (action: LineChangeAction, item: CatalogItem) => ({
        label: LINE_CHANGE_ACTION_LABEL[action],
        onClick: () => {
            if (action === 'change-phone') void swapInto(item);
            else if (action === 'retry') void choose(item);
            else
                void navigate({
                    to: '/wizard/s/$sessionId/$step',
                    params: { sessionId, step: stepAfterCheckout(session) },
                });
        },
    });

    const creditBubblesOf = (item: CatalogItem): readonly BenefitBubble[] =>
        notEligible || !sureKeepFinances(promoItem(item))
            ? []
            : cardCreditBubbles(item, scopeWide, promoLevel, levelPending);

    /** Lista de la tarjeta: las promos propias del SKU y luego las de crédito. */
    const cardPromosOf = (item: CatalogItem): readonly CardPromo[] | undefined => {
        const own = slots.cardPromotions?.(promoItem(item));
        return own && [...own, ...creditBubblesOf(item)];
    };

    const renderEntry = (entry: CatalogEntry): ReactNode => {
        if (entry.kind === 'item') {
            const benefits = slots.saleBenefits?.(promoItem(entry.item));
            const promos = cardPromosOf(entry.item);
            return (
                <ProductCard
                    item={entry.item}
                    selected={entry.item.sku === pickedSku}
                    terms={terms}
                    onSelect={setTouchedSku}
                    creditTerms={creditTerms}
                    onShowKitContents={setKitContentsSku}
                    {...(benefits && { saleBenefits: benefits })}
                    creditBubbles={creditBubblesOf(entry.item)}
                    {...(promos && { promos })}
                />
            );
        }
        const variant = variantOf(entry.group);
        const shown = variant ?? entry.group.defaultVariant;
        const benefits = slots.saleBenefits?.(promoItem(shown));
        const promos = cardPromosOf(shown);
        return (
            <ModelCard
                group={entry.group}
                variant={variant}
                selected={entry.group.variants.some((v) => v.sku === pickedSku)}
                terms={terms}
                busy={busy}
                reservedSku={sessionPhoneSku ?? null}
                onSelect={(item) => {
                    setTouchedSku(item.sku);
                }}
                onVariantChange={(item) => {
                    changeVariant(entry.group, item);
                }}
                onChoose={(item) => {
                    changeVariant(entry.group, item);
                    void choose(item);
                }}
                creditTerms={creditTerms}
                {...(benefits && { saleBenefits: benefits })}
                creditBubbles={creditBubblesOf(shown)}
                {...(promos && { promos })}
            />
        );
    };

    return (
        <div className="flex w-full max-w-6xl flex-col">
            <header className="mb-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Selección
                    </div>
                    <div className="mb-1 flex items-center gap-1.5">
                        <h1 className="text-[20px] font-semibold leading-[1.2] tracking-[-0.01em]">
                            Elige el teléfono
                        </h1>
                        <HowItWorksButton />
                    </div>
                    <p className="max-w-3xl text-[13px] leading-[1.5] text-muted-foreground">
                        Elige capacidad y color en cada tarjeta; toca el nombre para ver sus promos.
                    </p>
                </div>
                <div className="flex flex-none items-center gap-4 pt-1">
                    <WizardBackLink
                        sessionId={sessionId}
                        step={(session && previousStepSlug('product', session)) ?? 'score'}
                    />
                    <SaveAndExitButton />
                </div>
            </header>

            {/* El nivel va en la franja del cliente; aquí solo el tope de crédito. */}
            {sureKeep && catalog?.creditCapCents !== undefined && (
                <section
                    aria-label="Crédito del cliente"
                    className="mb-3 text-[12px] text-muted-foreground"
                >
                    Tope de crédito {formatMXN(catalog.creditCapCents / 100)}
                </section>
            )}

            {session && slots.referredByNotice?.(customerFirstName(session))}

            {/* Solo las de toda la tienda; las de un equipo van en su tarjeta y su detalle. */}
            <StorePromosBanner
                sale={slots.storePromotionGroups?.sale ?? []}
                accessories={slots.storePromotionGroups?.accessories ?? []}
                credit={storeCreditLines}
            />

            <CatalogToolbar
                filters={filters}
                onChange={setFilters}
                brands={brands}
                financiers={financiers}
                shown={entries.length}
                total={totalEntries}
                searchRef={searchRef}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    {isLoading ? (
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                            {Array.from({ length: SKELETONS }, (_, i) => (
                                <ProductCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : isError ? (
                        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-10 text-center">
                            <PackageX className="size-7 text-muted-foreground" aria-hidden />
                            <p className="text-[13px] text-muted-foreground">
                                No se pudo leer el catálogo de la tienda.
                            </p>
                            <Button
                                size="sm"
                                variant="secondary"
                                isLoading={isFetching}
                                onClick={() => void refetch()}
                            >
                                Volver a intentar
                            </Button>
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-10 text-center">
                            <SearchX className="size-7 text-muted-foreground" aria-hidden />
                            <p className="text-[13px] text-muted-foreground">
                                Ningún equipo coincide.
                            </p>
                        </div>
                    ) : (
                        <ul
                            aria-label="Equipos"
                            aria-busy={busy}
                            className={cn(
                                'grid auto-rows-fr grid-cols-2 gap-3 transition-opacity md:grid-cols-3',
                                busy && 'pointer-events-none opacity-60',
                            )}
                        >
                            {entries.map((entry) => (
                                <li key={entry.key} className="flex min-w-0">
                                    {renderEntry(entry)}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Una sola pieza sticky: el cuerpo hace scroll y el pie con «Continuar» queda visible. */}
                <section
                    aria-label="Detalle del equipo"
                    className="flex flex-col gap-3 lg:sticky lg:top-[4.25rem] lg:max-h-[calc(100dvh-5.25rem)] lg:self-start"
                >
                    <div className="flex min-h-0 flex-col gap-3 lg:flex-1 lg:overflow-y-auto">
                        <ProductDetailCard
                            picked={picked}
                            stock={picked?.stock ?? null}
                            reservation={pickedReservation}
                            imei={null}
                            clientLevel={promoLevel}
                            {...(picked !== null &&
                                slots.promotionPicker && {
                                    promotionPicker: slots.promotionPicker(
                                        promoItem(picked),
                                        detailContext,
                                    ),
                                })}
                            {...(picked !== null &&
                                slots.detailPromotions && {
                                    salePromotions: (renderBonuses: RenderAccountBonuses) =>
                                        slots.detailPromotions?.(
                                            promoItem(picked),
                                            detailContext,
                                            renderBonuses,
                                        ),
                                })}
                            {...(picked !== null &&
                                slots.accessoryPromotions && {
                                    accessoryPromotions: slots.accessoryPromotions(
                                        promoItem(picked),
                                    ),
                                })}
                            {...(pricing !== null && {
                                footer: (
                                    <div className="flex flex-col gap-2.5">
                                        {!isPickedKit &&
                                            slots.discountCode?.({
                                                sku: pricingSku,
                                                disabled: charged || busy,
                                            })}
                                        <PriceBreakdown
                                            pricing={pricing}
                                            sureKeepOnlyCents={sureKeepOnlyCents}
                                        />
                                    </div>
                                ),
                            })}
                            downPayment={pickedDownPayment}
                            {...(picked !== null && {
                                creditPromotions: creditRowsFor(picked),
                                creditNote: notEligible
                                    ? 'notEligible'
                                    : levelPending
                                      ? 'levelPending'
                                      : undefined,
                            })}
                            {...(picked !== null &&
                                isPickedKit && {
                                    kitComponents: kitComponentCount(picked),
                                    onShowKitContents: () => {
                                        setKitContentsSku(picked.sku);
                                    },
                                })}
                        />
                    </div>
                    {picked !== null && pricing !== null && (
                        <div className="flex flex-none flex-col gap-2.5">
                            <Button
                                isLoading={busy}
                                rightIcon={<ArrowRight className="size-4" aria-hidden />}
                                onClick={() => void choose(picked)}
                            >
                                Continuar
                            </Button>
                            {pickedReservation !== 'held' && (
                                <p className="text-center text-[11px] text-muted-foreground">
                                    Se aparta 30 min
                                </p>
                            )}
                        </div>
                    )}
                </section>
            </div>

            <ConfirmDialog
                open={kitReplaceFor !== undefined}
                onOpenChange={(open) => {
                    if (!open) setKitReplaceFor(undefined);
                }}
                title="¿Cambiar el kit por este teléfono?"
                description="La venta tiene un kit apartado. Si continúas, se suelta el kit y se aparta este teléfono."
                confirmLabel="Cambiar"
                cancelLabel="Conservar el kit"
                onConfirm={() => {
                    const item = kitReplaceFor;
                    setKitReplaceFor(undefined);
                    if (item) void choose(item, anchorKit?.lineId, true);
                }}
            />

            <ConfirmDialog
                open={discardPrompt !== undefined}
                onOpenChange={(open) => {
                    if (!open) answerDiscard(false);
                }}
                title="¿Descartar la cotización?"
                description={DISCARD_COPY[discardPrompt?.reason ?? 'promotions']}
                confirmLabel="Descartar y continuar"
                cancelLabel="Conservar"
                onConfirm={() => {
                    answerDiscard(true);
                }}
            />

            <KitContentsModal
                kit={kitInModal}
                items={items}
                discountedPriceCents={
                    kitInModal
                        ? (slots.saleBenefits?.(promoItem(kitInModal)).discountedPriceCents ?? null)
                        : null
                }
                busy={busy}
                onClose={() => {
                    setKitContentsSku(undefined);
                }}
                onChoose={() => {
                    if (kitInModal === null) return;
                    setTouchedSku(kitInModal.sku);
                    setKitContentsSku(undefined);
                    void choose(kitInModal);
                }}
            />
        </div>
    );
}

/** Desglose del precio: base, un renglón por promo y código, y lo que paga. */
function PriceBreakdown({
    pricing,
    sureKeepOnlyCents,
}: {
    pricing: PhonePricing;
    /** Lo que solo aplica con crédito SureKeep; con él se dice el precio de contado. */
    sureKeepOnlyCents: number;
}) {
    const { baseCents, rows, totalCents, estimated } = pricing;
    return (
        <dl aria-label="Desglose del precio" className="flex flex-col gap-1 text-[12.5px]">
            {rows.length > 0 && (
                <div className="flex items-baseline justify-between gap-2">
                    <dt className="text-muted-foreground">Precio base</dt>
                    <dd className="tabular-nums">{formatMXN(baseCents / 100)}</dd>
                </div>
            )}
            {rows.map((row) => (
                <div key={row.key} className="flex items-baseline justify-between gap-2">
                    <dt className="min-w-0 text-success">
                        <span className="block truncate" title={row.label}>
                            {row.kind === 'store' ? `Descuento de tienda: ${row.label}` : row.label}
                        </span>
                        {(row.note || row.sureKeepOnly) && (
                            <span className="block text-[11px] text-muted-foreground">
                                {[row.note, row.sureKeepOnly ? 'Solo con crédito SureKeep' : null]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </span>
                        )}
                    </dt>
                    <dd className="flex-none font-semibold tabular-nums text-success">
                        {row.cents !== null ? `−${formatMXN(row.cents / 100)}` : '—'}
                    </dd>
                </div>
            ))}
            <div className="flex items-baseline justify-between gap-2 border-t border-border pt-1.5">
                <dt className="flex flex-col">
                    <span className="font-semibold">Precio a pagar</span>
                    {estimated && rows.length > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                            Estimado; se confirma al continuar
                        </span>
                    )}
                </dt>
                <dd className="flex flex-col items-end">
                    <strong className="text-[20px] tabular-nums">
                        {formatMXN(totalCents / 100)}
                    </strong>
                    {sureKeepOnlyCents > 0 && (
                        <span
                            data-testid="picked-cash-price"
                            className="text-[11.5px] text-muted-foreground tabular-nums"
                        >
                            De contado {formatMXN((totalCents + sureKeepOnlyCents) / 100)}
                        </span>
                    )}
                </dd>
            </div>
        </dl>
    );
}
